import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, MapPin, Mail, Instagram, CheckCircle2 } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.2 1.6 3.7 3.7 3.9v2.6c-1.3.1-2.5-.3-3.7-1v5.9c0 3.4-2.5 5.6-5.7 5.6-3 0-5.3-2.3-5.3-5.2 0-3.1 2.4-5.1 5.6-4.9v2.7c-.4-.1-.9-.2-1.3-.1-1.2.1-2 .9-1.9 2.2.1 1.2 1 1.9 2.2 1.8 1.3-.1 2-1 2-2.4V3h2.4z" />
  </svg>
);

const LEVELS = [
  "Youth (12U)",
  "Middle School (13-14)",
  "High School — JV",
  "High School — Varsity",
  "College",
  "Independent / Pro",
];

const inputClass =
  "w-full border border-white/15 bg-transparent px-4 py-3.5 font-body text-sm text-white placeholder:text-white/30 transition-colors duration-200 focus:border-[#00F0FF] focus:outline-none";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    athlete_level: "",
    goal: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.athlete_level || !form.goal) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", athlete_level: "", goal: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-white/10 bg-[#080808] py-24 md:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:px-12 lg:grid-cols-2 lg:gap-16">
        {/* Left: copy + map + social */}
        <div>
          <Reveal>
            <SectionLabel>[ Booking / Contact ]</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-heading text-4xl font-black uppercase leading-none tracking-tight text-white md:text-6xl">
              Book your intro
              <br />
              <span className="text-[#00F0FF]">assessment</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-md font-body text-white/60 md:text-lg">
              Tell us about the athlete. We'll follow up to schedule a full biomechanical
              assessment and map out the fastest path to measurable gains.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-10 space-y-4">
              <a
                href="mailto:fuelfactory1@gmail.com"
                data-testid="contact-email-link"
                className="flex items-center gap-3 font-mono text-sm text-white/70 transition-colors hover:text-[#00F0FF]"
              >
                <Mail size={16} className="text-[#00F0FF]" />
                fuelfactory1@gmail.com
              </a>
              <div className="flex items-center gap-3 font-mono text-sm text-white/70">
                <MapPin size={16} className="text-[#00F0FF]" />
                Fairfield County, CT
              </div>
            </div>
          </Reveal>

          {/* Map placeholder */}
          <Reveal delay={0.35}>
            <div className="relative mt-8 h-56 overflow-hidden border border-white/10 grid-lines bg-[#0a0a0a]">
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <MapPin size={28} className="text-[#00F0FF]" />
                <span className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
                  Facility Map — Coming Soon
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="mt-8 flex items-center gap-4">
              {[
                { Icon: Instagram, label: "instagram", href: "https://instagram.com/the_fuel_factory_" },
                { Icon: TikTokIcon, label: "tiktok", href: "https://tiktok.com/@the.fuel.factory" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${label}`}
                  aria-label={label}
                  className="flex h-11 w-11 items-center justify-center border border-white/15 text-white/70 transition-colors hover:border-[#00F0FF] hover:text-[#00F0FF]"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Right: form */}
        <Reveal delay={0.2}>
          <div className="border border-white/10 bg-[#050505] p-6 md:p-10">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex h-full min-h-[420px] flex-col items-center justify-center text-center"
                data-testid="contact-success"
              >
                <CheckCircle2 size={56} className="text-[#00F0FF]" />
                <h3 className="mt-6 font-heading text-3xl font-black uppercase tracking-tight text-white">
                  Request Received
                </h3>
                <p className="mt-3 max-w-xs font-body text-sm text-white/60">
                  Thanks — we've got your details and will reach out shortly to lock in your
                  assessment.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="contact-reset"
                  className="mt-8 border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-[0.12em] text-white transition-colors hover:border-[#00F0FF] hover:text-[#00F0FF]"
                >
                  Submit Another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
                      Full Name
                    </label>
                    <input
                      data-testid="input-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Jordan Smith"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
                      Phone
                    </label>
                    <input
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="(555) 123-4567"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
                    Email
                  </label>
                  <input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@email.com"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
                    Athlete Age / Level
                  </label>
                  <Select
                    value={form.athlete_level}
                    onValueChange={(v) => update("athlete_level", v)}
                  >
                    <SelectTrigger
                      data-testid="select-level"
                      className="w-full rounded-none border-white/15 bg-transparent px-4 py-6 font-body text-sm text-white focus:border-[#00F0FF] focus:ring-0 data-[placeholder]:text-white/30"
                    >
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent className="border-white/15 bg-[#0a0a0a] text-white">
                      {LEVELS.map((l) => (
                        <SelectItem key={l} value={l} className="font-body focus:bg-[#00F0FF] focus:text-black">
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
                    Primary Goal
                  </label>
                  <textarea
                    data-testid="input-goal"
                    value={form.goal}
                    onChange={(e) => update("goal", e.target.value)}
                    rows={3}
                    placeholder="e.g. Add 4+ mph before spring season, clean up my mechanics..."
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="lead-submit-button"
                  className="group inline-flex w-full items-center justify-center gap-3 bg-[#00F0FF] px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.12em] text-black transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Book Performance Assessment"}
                  {!loading && (
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
